import { Box, FormControl } from '@chakra-ui/react';
import React, { useEffect } from 'react';

import { finleyAssertUnreachable } from '@finley-technologies/common';
import { CreditAgreement } from '@finley-technologies/finley-types';

import { FiltersControl } from './FiltersEditor';
import { renderConfigSwitch } from './renderConfigSwitch';
import { SourceConfigTypeEditor } from './SourceConfigTypeEditor';

const createShellConfig = (
  calculationType: CreditAgreement.Config.ConcentrationLimit.Calculation.SourceConfigType,
): CreditAgreement.Config.ConcentrationLimit.Calculation.SourceConfig => {
  const placeHolderFieldName = 'placeholder_field';
  switch (calculationType) {
    case 'standard':
      return {
        type: 'standard',
        config: {
          filters: [],
        },
      };
    case 'weightedAverage':
      return {
        type: 'weightedAverage',
        limitType: 'min',
        config: {
          weighting: 'eligibleExposureLessConcentrationKickout',
          field: placeHolderFieldName,
          filters: [],
        },
      };
    case 'exposureByGroup':
      return {
        type: 'exposureByGroup',
        config: {
          groupingField: placeHolderFieldName,
          fieldType: 'string',
          filters: [],
        },
      };
    case 'directInsert':
      return {
        type: 'directInsert',
        config: {
          kickoutValueField: placeHolderFieldName,
          filters: [],
        },
      };
    case 'pointer':
      return {
        type: 'pointer',
        config: {
          method: 'placeholder_method',
        },
      };
    default:
      // this assert is fine.
      return finleyAssertUnreachable(calculationType);
  }
};
export const CalculationSourceConfigEditor = ({
  value,
  availableFields,
  onChange,
}: {
  value: CreditAgreement.Config.ConcentrationLimit.Calculation.SourceConfig;
  availableFields: CreditAgreement.Config.ConcentrationLimit.Calculation.AvailableField[];
  onChange: (
    newValue: CreditAgreement.Config.ConcentrationLimit.Calculation.SourceConfig,
  ) => void;
}) => {
  const [
    currentConfig,
    setCurrentConfig,
  ] = React.useState<CreditAgreement.Config.ConcentrationLimit.Calculation.SourceConfig>(
    value,
  );

  // we need to maintain calculation Type and corresponding config
  // when they change calculationType we need to extract or create existing config associated with that calculation Type
  // that way accidentally toggling between calculation types will not lose the config
  const [calculationTypeToConfigMap] = React.useState<
    Record<
      CreditAgreement.Config.ConcentrationLimit.Calculation.SourceConfigType,
      CreditAgreement.Config.ConcentrationLimit.Calculation.SourceConfig
    >
  >({
    standard: createShellConfig('standard'),
    weightedAverage: createShellConfig('weightedAverage'),
    exposureByGroup: createShellConfig('exposureByGroup'),
    pointer: createShellConfig('pointer'),
    directInsert: createShellConfig('directInsert'),
  });

  useEffect(() => {
    // if the value changes, we need to update the calculation type
    // to match the new calculation type
    setCurrentConfig(value);
  }, [value]);

  const updateCalculationType = (
    newValue: CreditAgreement.Config.ConcentrationLimit.Calculation.SourceConfigType,
  ) => {
    const newConfig = calculationTypeToConfigMap[newValue];
    setCurrentConfig(newConfig);
    onChange(newConfig);
  };

  return (
    <Box p={1}>
      <SourceConfigTypeEditor
        value={currentConfig.type}
        onChange={updateCalculationType}
      />

      {/* Calculation Config Section */}
      {renderConfigSwitch(value, availableFields, onChange)}

      {/* Filters Section */}
      <FormControl mb={4}>
        <FiltersControl
          filters={value.config.filters || []}
          availableFields={availableFields}
          onChange={(newValue) => {
            switch (value.type) {
              case 'standard':
                onChange({
                  ...value,
                  config: {
                    ...value.config,
                    filters: newValue,
                  },
                });
                return;
              case 'weightedAverage':
                onChange({
                  ...value,
                  config: {
                    ...value.config,
                    filters: newValue,
                  },
                });
                return;
              case 'exposureByGroup':
                onChange({
                  ...value,
                  config: {
                    ...value.config,
                    filters: newValue,
                  },
                });
                return;
              case 'directInsert':
                onChange({
                  ...value,
                  config: {
                    ...value.config,
                    filters: newValue,
                  },
                });
                return;
              default:
                // cant assert here, because JSON editor may allows it.
                onChange({
                  ...value,
                  config: {
                    ...value.config,
                    filters: newValue,
                  },
                });
            }
          }}
        />
      </FormControl>
    </Box>
  );
};
